import {
  Box,
  Button,
  Center,
  HStack,
  Spinner,
  Stack,
  Text,
  useBreakpointValue,
  Wrap,
} from "@chakra-ui/react";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { FormattedMessage } from "react-intl";
import useDownloadFile from "../../../../hooks/useDownloadFile";
import generateApiUri from "../../../../libraries/utils/generateApiUri";
import SessionsRegistrationsDatesActionDelete from "../../../CustomersDocumentsPages/Actions/Delete";
import CustomersDocumentsPagesActionDownload from "../../../CustomersDocumentsPages/Actions/Download";
import ImageRenderer from "./Renderers/ImageRenderer";

const Page = memo(
  /**
   * @typedef {Object} Props
   * @property {import("../../../../types/CustomerDocumentPage").CustomerDocumentPage} page
   * @property {() => void} mutateCustomerDocument
   *
   * @param {Props} props
   */
  function Page({ page, mutateCustomerDocument }) {
    const [blob, setBlob] = useState(/** @type {Blob | null} */ (null));

    const onSuccess = useCallback(({ blob }) => {
      setBlob(blob);
    }, []);

    const { download, isLoading } = useDownloadFile({
      mode: "noop",
      onSuccess,
    });

    const isMobile = useBreakpointValue({ base: true, md: false });

    useEffect(() => {
      download(
        generateApiUri({
          id: "@customers.documents.pages.download",
          parameters: {
            pageId: page.id,
          },
        }),
      );
    }, [download, page.id]);

    const blobUrl = useMemo(() => {
      return blob ? window.URL.createObjectURL(blob) : null;
    }, [blob]);

    const onSuccessDelete = useCallback(() => {
      mutateCustomerDocument();
    }, [mutateCustomerDocument]);

    return (
      <Stack flexGrow={1} spacing="0">
        <HStack justify="flex-end" spacing="0">
          <Box flexGrow={1} borderBottomWidth="1px" h="full" />

          <Box
            borderWidth="1px"
            borderBottomWidth="0"
            py=".5rem"
            px="1rem"
            borderTopRadius="6px"
            backgroundColor="blackAlpha.200">
            <Wrap justify="flex-end" align="baseline">
              <CustomersDocumentsPagesActionDownload page={page}>
                {({ isAllowed, download, isLoading }) =>
                  isAllowed ? (
                    <Button
                      onClick={download}
                      isLoading={isLoading}
                      size="sm"
                      colorScheme="gray">
                      <FormattedMessage defaultMessage="Télécharger" />
                    </Button>
                  ) : null
                }
              </CustomersDocumentsPagesActionDownload>

              <SessionsRegistrationsDatesActionDelete
                page={page}
                onSuccess={onSuccessDelete}>
                {({ isAllowed, trigger }) =>
                  isAllowed ? (
                    <Button onClick={trigger} size="sm" colorScheme="red">
                      <FormattedMessage defaultMessage="Supprimer" />
                    </Button>
                  ) : null
                }
              </SessionsRegistrationsDatesActionDelete>
            </Wrap>
          </Box>
        </HStack>

        <Box
          position="relative"
          backgroundColor="blackAlpha.200"
          borderLeftWidth="1px"
          borderRightWidth="1px"
          borderBottomWidth="1px"
          flexGrow={1}
          flexBasis={0}
          overflow="hidden">
          {blobUrl && (
            <>
              {(() => {
                if (blob?.type.startsWith("image/")) {
                  return <ImageRenderer blobUrl={blobUrl} />;
                }
                if (blob?.type.startsWith("application/pdf") && !isMobile) {
                  return (
                    <Box
                      as="object"
                      data={blobUrl}
                      objectFit="scale-down"
                      w="full"
                      maxW="full"
                      h="full"
                      maxH="full"
                    />
                  );
                }
                return (
                  <Center h="full">
                    <Text>
                      <FormattedMessage defaultMessage="Prévisualisation indisponible" />
                    </Text>
                  </Center>
                );
              })()}
            </>
          )}

          {isLoading && (
            <Center position="absolute" w="full" h="full">
              <Spinner />
            </Center>
          )}
        </Box>
      </Stack>
    );
  },
);

export default Page;
